import type {InspectionDispatchCommitRequest} from 'src/api/ProactApiClient';

// A tentative, unsaved change to an inspection's dispatch state. `place` assigns
// an inspector + scheduled start; `unassign` returns a committed one to the queue.
export type TentativeChange =
  | {type: 'place'; assigneeId: number; scheduledStartAt: Date}
  | {type: 'unassign'};

export interface TentativeState {
  changes: Record<number, TentativeChange>;
}

export type TentativeAction =
  | {type: 'place'; inspectionId: number; assigneeId: number; scheduledStartAt: Date}
  | {type: 'setTime'; inspectionId: number; scheduledStartAt: Date}
  | {type: 'unassign'; inspectionId: number}
  | {type: 'remove'; inspectionId: number}
  | {type: 'reset'};

export const initialTentativeState: TentativeState = {changes: {}};

export function tentativeReducer(
  state: TentativeState,
  action: TentativeAction
): TentativeState {
  switch (action.type) {
    case 'place':
      return {
        changes: {
          ...state.changes,
          [action.inspectionId]: {
            type: 'place',
            assigneeId: action.assigneeId,
            scheduledStartAt: action.scheduledStartAt,
          },
        },
      };
    case 'setTime': {
      const existing = state.changes[action.inspectionId];
      if (!existing || existing.type !== 'place') return state;
      return {
        changes: {
          ...state.changes,
          [action.inspectionId]: {
            ...existing,
            scheduledStartAt: action.scheduledStartAt,
          },
        },
      };
    }
    case 'unassign':
      return {
        changes: {
          ...state.changes,
          [action.inspectionId]: {type: 'unassign'},
        },
      };
    case 'remove': {
      const next = {...state.changes};
      delete next[action.inspectionId];
      return {changes: next};
    }
    case 'reset':
      return initialTentativeState;
    default:
      return state;
  }
}

export const tentativeCount = (state: TentativeState): number =>
  Object.keys(state.changes).length;

export const buildCommitRequest = (
  state: TentativeState
): InspectionDispatchCommitRequest => {
  const placements: InspectionDispatchCommitRequest['placements'] = [];
  const unassign: number[] = [];
  for (const [id, change] of Object.entries(state.changes)) {
    const inspectionId = Number(id);
    if (change.type === 'place') {
      placements.push({
        inspectionId,
        assigneeId: change.assigneeId,
        scheduledStartAt: change.scheduledStartAt,
      });
    } else {
      unassign.push(inspectionId);
    }
  }
  return {placements, unassign};
};
